import React, { useEffect, useState } from 'react';
import { MatrixType } from '../../lib/url/matrixMapping';
import { MatrixSelectionManager, MatrixMenuState } from '../../lib/matrix/MatrixSelectionManager';
import { MATRIX_DEFINITIONS } from '../../lib/url/matrixMapping';

interface MatrixSelectorProps {
  manager: MatrixSelectionManager;
  onSelectionChange?: (matrix: MatrixType) => void;
}

export const MatrixSelector: React.FC<MatrixSelectorProps> = ({ manager, onSelectionChange }) => {
  const [menuState, setMenuState] = useState<MatrixMenuState>(manager.getMenuState());

  // Update menu state when manager's state changes
  useEffect(() => {
    const handleStateChange = () => {
      const newState = manager.getMenuState();
      // Only update state and trigger callback when the change is complete
      if (!newState.isUpdating) {
        setMenuState(newState);
        if (newState.selectedMatrix !== menuState.selectedMatrix) {
          onSelectionChange?.(newState.selectedMatrix);
        }
      } else {
        // Just update the UI state for loading indicator
        setMenuState(newState);
      }
    };

    // Initial state
    handleStateChange();

    // Subscribe to manager state changes
    const unsubscribe = manager.addChangeListener(handleStateChange);

    // Subscribe to storage changes for cross-tab sync
    window.addEventListener('storage', handleStateChange);
    
    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStateChange);
    };
  }, [manager, onSelectionChange, menuState.selectedMatrix]);

  const handleSelection = async (newMatrix: MatrixType) => {
    try {
      await manager.handleMenuSelection(newMatrix);
    } catch (error) {
      console.error('Failed to update matrix selection:', error);
    }
  };

  // Group matrices by their group property
  const groupedMatrices = manager.getAvailableMatrices().reduce((acc, matrix) => {
    const group = MATRIX_DEFINITIONS[matrix].group;
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(matrix);
    return acc;
  }, {} as Record<string, MatrixType[]>);

  // Define group labels
  const groupLabels: Record<string, string> = {
    railNetwork: 'Rail Network',
    railPassengers: 'Rail Passengers',
    trains: 'Trains',
    railEconomics: 'Rail Economics',
    comparison: 'Comparison',
    emissions: 'Emissions'
  };

  // Define group order
  const groupOrder = [
    'railNetwork',
    'railPassengers',
    'trains',
    'railEconomics',
    'comparison',
    'emissions'
  ];

  return (
    <div className="relative">
      <select
        value={menuState.selectedMatrix}
        onChange={(e) => handleSelection(e.target.value as MatrixType)}
        disabled={menuState.isUpdating}
        className={`
          p-2 pr-8 border rounded min-w-[200px] appearance-none leading-5
          ${menuState.isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
          ${menuState.error ? 'border-red-500' : 'border-gray-300'}
        `}
      >
        {groupOrder.map(group => (
          <optgroup key={group} label={groupLabels[group]}>
            {groupedMatrices[group]?.map(matrix => (
              <option key={matrix} value={matrix}>
                {manager.getMatrixDisplayName(matrix)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
        <svg 
          className={`h-4 w-4 ${menuState.isUpdating ? 'animate-spin' : ''} ${menuState.error ? 'text-red-500' : 'text-gray-400'}`} 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          {menuState.isUpdating ? (
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          ) : (
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          )}
        </svg>
      </div>
      {menuState.error && (
        <div className="absolute top-full left-0 mt-1 text-sm text-red-500">
          {menuState.error}
        </div>
      )}
    </div>
  );
}; 